import { Link } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import { formatCaregiverProfileValue } from "../../../caregiverProfileModal.utils";
import { useCaregiverAssociatedPatients } from "./CaregiverProfilePatientsSection.hooks";
import type { CaregiverAssociatedPatient } from "./caregiverPatients.types";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiverId: CaregiverId;
};

function getPatientDisplayName(patient: CaregiverAssociatedPatient) {
  if (patient.patientDisplayName) {
    return patient.patientDisplayId
      ? `${patient.patientDisplayName} (${patient.patientDisplayId})`
      : patient.patientDisplayName;
  }

  const name = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(" ");

  const displayId = patient.patientDisplayId ?? patient.displayId;

  return displayId ? `${name} (${displayId})` : name;
}

function formatDate(value: unknown) {
  if (formatCaregiverProfileValue(value) === "") {
    return "";
  }

  if (value instanceof Object) {
    return dateFormatter.toDate(value as never);
  }

  if (typeof value === "string") {
    try {
      return dateFormatter.toDate(LocalDate.parse(value));
    } catch {
      return value;
    }
  }

  return String(value);
}

function getPatientHref(patientId: CaregiverAssociatedPatient["patientId"]) {
  return `${window.location.protocol}//${window.location.host}${window.location.pathname}?patient=${patientId}`;
}

export default function CaregiverProfilePatientsSection(props: Props) {
  const patientsQuery = useCaregiverAssociatedPatients(props.caregiverId);
  const patients = patientsQuery.data?.patients ?? [];

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load associated patients."
      isError={patientsQuery.isError}
      isLoading={patientsQuery.isLoading}
      loadingLabel="Loading associated patients"
      onRetry={() => void patientsQuery.refetch()}
    >
      <EntityProfileDataTable
        columns={[
          {
            cellProps: { textAlign: "left" },
            header: "Patient",
            headerProps: { textAlign: "left" },
            key: "patient",
            render: (patient) => (
              <Link
                _hover={{ color: "#23527c", textDecoration: "none" }}
                borderBottom="1px solid currentColor"
                color="#337ab7"
                display="inline"
                fontSize="13px"
                href={getPatientHref(patient.patientId)}
                lineHeight="1.2"
                textDecoration="none"
                whiteSpace="normal"
                onClick={(event) => {
                  event.stopPropagation();
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                  }

                  event.preventDefault();
                  props.actions.navigateToPatient(patient.patientId);
                }}
              >
                {getPatientDisplayName(patient)}
              </Link>
            ),
            sortKey: "patient",
            sortValue: (patient) => getPatientDisplayName(patient),
          },
          {
            header: "Start date",
            key: "from",
            render: (patient) => formatDate(patient.from),
            sortKey: "from",
            sortValue: (patient) => String(patient.from ?? ""),
          },
          {
            header: "End date",
            key: "to",
            render: (patient) => formatDate(patient.to),
            sortKey: "to",
            sortValue: (patient) => String(patient.to ?? ""),
          },
          {
            header: "Visits Count",
            key: "visitsCount",
            render: (patient) => patient.visitsCount,
            sortKey: "visitsCount",
          },
        ]}
        defaultSort={{ direction: "asc", key: "from" }}
        emptyLabel="No associated patients."
        getRowKey={(patient) => patient.patientId}
        pageSizeOptions={[10, 25, 50, 100]}
        rowProps={(patient) => ({
          _hover: { bg: "#fafafa" },
          cursor: "pointer",
          onClick: () => props.actions.navigateToPatient(patient.patientId),
        })}
        rows={patients}
      />
    </EntityProfileQuerySection>
  );
}
